import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { LogOut, LayoutGrid, ShieldCheck, BookKey, FolderKanban, Crown, Settings as SettingsIcon, BarChart3, Activity, MapPin } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Toaster } from "sonner";
import HeartbeatTracker from "./HeartbeatTracker";
import WelcomeGrowthModal from "./WelcomeGrowthModal";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-link">
            <img
              src="/array-logo.png"
              alt="Array Engine"
              className="h-7 w-auto"
            />
          </Link>
          <nav className="flex items-center gap-1">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `text-sm px-3 py-2 rounded-sm flex items-center gap-2 ${
                  isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"
                }`
              }
              data-testid="nav-dashboard"
            >
              <LayoutGrid className="w-4 h-4" /> Dashboard
            </NavLink>
            <NavLink
              to="/programs"
              className={({ isActive }) =>
                `text-sm px-3 py-2 rounded-sm flex items-center gap-2 ${
                  isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"
                }`
              }
              data-testid="nav-programs"
            >
              <FolderKanban className="w-4 h-4" /> Programs
            </NavLink>
            <NavLink
              to="/key-dictionary"
              className={({ isActive }) =>
                `text-sm px-3 py-2 rounded-sm flex items-center gap-2 ${
                  isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"
                }`
              }
              data-testid="nav-key-dictionary"
            >
              <BookKey className="w-4 h-4" /> Key dictionary
            </NavLink>
            <NavLink
              to="/map"
              className={({ isActive }) =>
                `text-sm px-3 py-2 rounded-sm flex items-center gap-2 ${
                  isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"
                }`
              }
              data-testid="nav-map"
            >
              <MapPin className="w-4 h-4" /> Map
            </NavLink>
            <NavLink
              to="/my-analytics"
              className={({ isActive }) =>
                `text-sm px-3 py-2 rounded-sm flex items-center gap-2 ${
                  isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"
                }`
              }
              data-testid="nav-my-analytics"
            >
              <Activity className="w-4 h-4" /> Analytics
            </NavLink>
          </nav>
          <div className="flex items-center gap-3">
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2.5 hover:bg-slate-50 px-2 py-1 rounded-sm transition-colors" data-testid="header-user-menu-trigger">
                    {user.profile_picture ? (
                      <img
                        src={user.profile_picture}
                        alt={user.name}
                        className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200"
                        data-testid="header-avatar"
                      />
                    ) : null}
                    <div className="text-right">
                      <div className="text-sm font-medium leading-none" data-testid="header-user-name">{user.name}</div>
                      <div className="label-overline mt-1 flex items-center gap-1 justify-end">
                        {user.org_name ? (
                          <span data-testid="header-org-name">{user.org_name}</span>
                        ) : (
                          <><ShieldCheck className="w-3 h-3" /> {user.role}</>
                        )}
                      </div>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[200px]">
                  <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
                    Signed in as <span className="font-medium text-slate-800">{user.email}</span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                    <SettingsIcon className="w-4 h-4 mr-2" /> Settings
                  </DropdownMenuItem>
                  {user?.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin/analytics")} data-testid="menu-admin-analytics">
                      <BarChart3 className="w-4 h-4 mr-2 text-emerald-600" /> Platform Analytics
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="menu-upgrade-plan">
                    <Crown className="w-4 h-4 mr-2 text-emerald-600" /> Upgrade Plan
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-rose-600 focus:text-rose-700">
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-10">{children}</main>
      {user && <HeartbeatTracker />}
      {user && <WelcomeGrowthModal />}
      <Toaster richColors position="top-right" closeButton />
      <footer className="border-t border-slate-200 mt-16">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between text-xs text-slate-500">
          <span className="label-overline">Array Engine · Built for impact</span>
          <span>Every number maps to source data.</span>
        </div>
      </footer>
    </div>
  );
}
